import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

type OnboardingHighlightContextValue = {
  highlightTargetId: string | null
  setHighlightTargetId: (id: string | null) => void
  /** Call before setHighlightTargetId from a click handler so that click doesn't clear the highlight. */
  ignoreNextDocumentClick: () => void
}

const OnboardingHighlightContext = createContext<OnboardingHighlightContextValue | null>(null)

export const useOnboardingHighlight = (): OnboardingHighlightContextValue => {
  const ctx = useContext(OnboardingHighlightContext)
  if (!ctx) {
    throw new Error('useOnboardingHighlight must be used within OnboardingHighlightProvider')
  }
  return ctx
}

/**
 * Optional hook for components that only need to set the highlight (e.g. ExplainerBlockCard).
 * Returns null if outside provider so callers can guard.
 */
export const useOnboardingHighlightSetter = (): OnboardingHighlightContextValue | null => {
  return useContext(OnboardingHighlightContext)
}

type OnboardingHighlightProviderProps = {
  children: React.ReactNode
}

export const OnboardingHighlightProvider: React.FC<OnboardingHighlightProviderProps> = ({ children }) => {
  const [highlightTargetId, setHighlightTargetIdState] = useState<string | null>(null)
  const ignoreNextClickRef = useRef(false)
  const timeoutRef = useRef<number | null>(null)

  const setHighlightTargetId = useCallback((id: string | null) => {
    setHighlightTargetIdState(id)
  }, [])

  const ignoreNextDocumentClick = useCallback(() => {
    ignoreNextClickRef.current = true
  }, [])

  useEffect(() => {
    // Clear any existing timeout whenever the target changes
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (highlightTargetId == null) {
      return
    }

    const handleClick = (e: MouseEvent) => {
      if (ignoreNextClickRef.current) {
        ignoreNextClickRef.current = false
        return
      }
      setHighlightTargetIdState(null)
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setHighlightTargetIdState(null)
      }
    }

    // Auto-clear after 5 seconds
    timeoutRef.current = window.setTimeout(() => {
      setHighlightTargetIdState(null)
      timeoutRef.current = null
    }, 5000)

    document.addEventListener('click', handleClick, true)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('keydown', handleKeyDown)

      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [highlightTargetId])

  const value: OnboardingHighlightContextValue = {
    highlightTargetId,
    setHighlightTargetId,
    ignoreNextDocumentClick,
  }

  return <OnboardingHighlightContext.Provider value={value}>{children}</OnboardingHighlightContext.Provider>
}
